import { ChangeDetectorRef, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserEntity } from '@core/entities';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GetCredentials, isMobileView } from '@core/utils';
import { HumanizePipe } from '@shared/pipes';
import { UseUser } from '@core/usecases';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DefaultImageDirective } from '@shared/directives';
import { IconComponent } from '@shared/components/ui';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'app-appointment-card',
  imports: [CommonModule, IconComponent, TranslateModule, HumanizePipe, DefaultImageDirective],
  templateUrl: './appointment-card.component.html',
  styleUrls: ['./appointment-card.component.scss'],
})
export class AppointmentCardComponent implements OnChanges {
  @Input() isActive = false;
  @Input() user: UserEntity;
  private readonly _translate = inject(TranslateService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);
  private readonly _useUser = new UseUser();

  get currentUsername() {
    return GetCredentials().username;
  }

  get remainingDays() {
    const dateToUse = this._getDateToUse();
    return dateToUse ? this._calculateRemainingDays(dateToUse) : 'No date provided or conditions not met';
  }

  get progressPercentageByCalculatingRemainingDays() {
    const dateToUse = this._getDateToUse();
    if (!dateToUse) return 0;

    const currentDate = new Date();
    dateToUse.setFullYear(currentDate.getFullYear());

    let diffTime = dateToUse.getTime() - currentDate.getTime();
    if (diffTime < 0) {
      dateToUse.setFullYear(currentDate.getFullYear() + 1);
      diffTime = dateToUse.getTime() - currentDate.getTime();
    }

    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const totalDays = 30;
    const remainingDays = Math.max(diffDays, 0);
    const progressPercentage = (remainingDays / totalDays) * 100;

    return Math.min(progressPercentage, 100);
  }

  get age() {
    const dateToUse = this._getDateToUse();
    if (!dateToUse) return 0;

    const currentDate = new Date();
    const diffTime = currentDate.getTime() - dateToUse.getTime();
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25); // Considering leap years
    return Math.floor(diffYears);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this._checkAndSetActive();
    }
  }

  navigateToChat(): void {
    const customParams = this.user.isBirthday ? { userId: this.user.id, isBirthday: true } : this.user.isJubilee ? { userId: this.user.id, isJubilee: true } : null;
    const route = isMobileView() ? '/m/messages/chat' : '/messages/chat';
    this._router.navigate([route], { queryParams: customParams });
  }

  private _getDateToUse(): Date | null {
    if (this.user?.isBirthday && this.user.bdNotification) {
      return new Date(this.user.bdNotification);
    } else if (this.user?.isJubilee && this.user.createdAt) {
      return new Date(this.user.createdAt);
    }
    return null;
  }

  private _checkAndSetActive(): void {
    const dateToUse = this._getDateToUse();
    if (dateToUse) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dateToUse.setFullYear(today.getFullYear());
      dateToUse.setHours(0, 0, 0, 0);

      this.isActive = dateToUse.getTime() === today.getTime();
    } else {
      this.isActive = false;
    }

    this._fetchUserImage();
  }

  private _calculateRemainingDays(dateToUse: Date): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateToUse.setHours(0, 0, 0, 0);

    dateToUse.setFullYear(today.getFullYear());

    const diffTime = dateToUse.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    // Check if the adjusted date is still in the past but the original month is the current month
    if (diffDays < 0 && dateToUse.getMonth() === today.getMonth()) {
      return `${Math.abs(diffDays)} ${this._translate.instant('days ago')}`;
    } else if (dateToUse.getMonth() !== today.getMonth()) {
      return 'Event not in current month';
    } else if (diffDays === 0) {
      this.isActive = true; // Set isActive to true if the event is today
      return 'Today';
    } else {
      return `${diffDays} ${this._translate.instant('days left')}`;
    }
  }

  private _fetchUserImage() {
    if (this.user.memberId) {
      // Fetch user image based on memberId
      this._useUser
        .getUserPhoto(+this.user.memberId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (photo) => {
            this.user.imageUrl = photo;
            this._cdr.markForCheck();
          },
        });
    }
  }
}
